import { StandardTableConfig } from "@stenajs-webui/grid";
import { Indent, Txt } from "@stenajs-webui/core";
import { RebateTableActionMenu } from "../components/RebateTableActionMenu";
import { RebateProgress } from "@/gql/graphql";
import { IndentValues } from "@/lib/utils/IndentValues";
import { formatDateRange } from "@/lib/formatters/DateFormatter";
import { Tooltip } from "@stenajs-webui/tooltip";
import { formatPeriod, resolveRebateModel } from "../lib/formatter";
import { RebateCompletionCell } from "../components/table/RebateCompletionCell";
import { CellWithTooltip } from "../components/table/CellWithTooltip";
import {
  resolvePeriodSortValue,
  resolveCompletionBackgroundColor,
} from "../lib/rebateProgressTableUtils";

type RebateColumns =
  | "agreementNumber"
  | "agreementName"
  | "criteria"
  | "description"
  | "criteriaModel"
  | "period"
  | "target"
  | "expectedVolume"
  | "suggestedExpectedVolume"
  | "achievedVolume"
  | "completion"
  | "actions";

export const createTableConfig = (
  onUpdateExpectedVolume: (rebateProgress: RebateProgress) => void
): StandardTableConfig<RebateProgress, RebateColumns> => ({
  keyResolver: (item) => item.goalId,
  disableInfiniteList: true,
  stickyHeader: true,
  columns: {
    agreementNumber: {
      itemValueResolver: (item) =>
        item.rebateAgreement?.rebateAgreementNumber ?? "",
      columnLabel: "No.",
    },
    agreementName: {
      itemValueResolver: (item) => item.rebateAgreement?.name ?? "",
      columnLabel: "Agreement",
    },
    criteria: {
      itemValueResolver: (item) => item.criteria?.name,
      columnLabel: "Criteria",
    },
    description: {
      itemValueResolver: (item) => item.criteria?.description,
      columnLabel: "Description",
    },
    criteriaModel: {
      itemValueResolver: (item) => item.criteria?.rebateModelCode,
      columnLabel: "Model",
      renderCell: ({ value }) => {
        const rebateModel = resolveRebateModel(value);

        return (
          <CellWithTooltip
            tooltipLabel={rebateModel.description}
            value={rebateModel.code ?? ""}
          />
        );
      },
      infoIconTooltipText: "Hover for code description",
    },
    period: {
      itemValueResolver: (item) =>
        resolvePeriodSortValue(item.goal?.period, item.goal?.rebateTermCode),
      columnLabel: "Period",
      renderCell: ({ item }) => {
        const valueFormatted = formatPeriod(
          item.goal?.period,
          item.goal?.rebateTermCode
        );
        const validFormatted = formatDateRange(
          item.goal?.validFrom,
          item.goal?.validTo
        );
        return (
          <CellWithTooltip
            tooltipLabel={validFormatted}
            value={valueFormatted}
          />
        );
      },
      infoIconTooltipText: "Hover for valid dates",
    },
    target: {
      itemValueResolver: (item) => {
        const first = item.displayedTarget.split("/")[0].trim();
        return Number(first) || 0;
      },
      columnLabel: "Target",
      renderCell: ({ item }) => (
        <Tooltip label={item.displayedTarget} appendTo={document.body}>
          <Indent num={IndentValues.EIGHT} maxWidth="200px">
            <Txt
              style={{ textOverflow: "ellipsis", overflow: "hidden" }}
              whiteSpace={"nowrap"}
            >
              {item.displayedTarget}
            </Txt>
          </Indent>
        </Tooltip>
      ),
    },
    expectedVolume: {
      itemValueResolver: (item) => item.expectedVolume,
      columnLabel: "Expected",
    },
    suggestedExpectedVolume: {
      itemValueResolver: (item) => item.suggestedExpectedVolume,
      columnLabel: "Suggested",
    },
    achievedVolume: {
      itemValueResolver: (item) => item.achieved,
      columnLabel: "Achieved",
    },
    completion: {
      itemValueResolver: (item) => item.completionPercent,
      columnLabel: "Completion",
      backgroundResolver: resolveCompletionBackgroundColor,
      renderCell: ({ item }) => <RebateCompletionCell rebateProgress={item} />,
    },
    actions: {
      itemValueResolver: (item) => "",
      width: "42px",
      columnLabel: "",
      justifyContentCell: "center",
      renderCell: ({ item }) => (
        <RebateTableActionMenu
          rebate={item}
          onUpdateExpectedVolume={onUpdateExpectedVolume}
        />
      ),
    },
  },
  columnGroups: {
    agreement: {
      columnOrder: ["agreementNumber", "agreementName"],
    },
    criteria: {
      columnOrder: ["criteria", "description", "period", "criteriaModel"],
    },
    volumes: {
      borderLeft: true,
      label: "Volumes",
      columnOrder: [
        "target",
        "expectedVolume",
        "suggestedExpectedVolume",
        "achievedVolume",
      ],
    },
    status: {
      borderLeft: true,
      columnOrder: ["completion"],
    },
    actions: {
      columnOrder: ["actions"],
    },
  },
  columnGroupOrder: ["agreement", "criteria", "volumes", "status", "actions"],
});
